import React, { useState, useEffect } from 'react';
import { formFieldTypes, formStringFieldPatterns, formModes, resources } from '../../constants.js';
import i18next from "../../i18n.js";
import { DateTimeInput } from '../forms/DateTimeInput.js';
import { TextAreaInput } from '../forms/TextAreaInput.js';
import { onKeyDown, loadAvatar } from '../helpers/HelperFunctions.js';
import CropInput from '../forms/CropInput.js';
import AttachmentsForm from '../forms/AttachmentsForm.js';
import AttachmentsInput from '../forms/AttachmentsInput.js';
import UserInput from '../forms/UserInput.js';
import WarningCheckBox from '../forms/WarningCheckBox.js';
import { DoubleCheckboxInput } from '../forms/DoubleCheckboxInput.js';
import DocumentSelectForm from '../forms/DocumentSeletForm.js';
import { FloatField } from '../forms/FloatField.js';
import SelectInput from '../forms/SelectInput.js';
import TripDestinationInput from '../forms/TripDestinationInput.js';
import StartingPointInput from '../forms/StartingPointInput.js';
import DateIntervalInput from '../forms/DateIntervalInput.js';
import RouteInput from '../forms/RouteInput.js';
import InfoBox from '../forms/InfoBox.js';
import SwitcherInput from '../forms/SwitcherInput.js';
import CollapsedSelectInput from '../forms/CollapsedSelectInput.js';
import RadioButtonGroupInput from '../forms/RadioButtonGroupInput.js';
import TransportSelectInput from '../forms/TransportSelectInput.js';
import SubForm from './SubForm.js';
import { InputMask } from '@react-input/mask';

const FormField = (props) => {
    const [userImage, setUserImage] = useState('');

    useEffect(() => {
        if (props.settings?.type === formFieldTypes.user && props.settings.userLogin) {
            loadAvatar(props.settings.userLogin, props.settings.avatarId).then(url => {
                setUserImage(url ?? resources.defaulUserImg);
            });
        }
    }, []);

    const changeInputValueHandle = (e) => {
        e.target.value = e.target.value.replaceAll(' ', '');
        const { value } = e.target;
        if ((/^[0-9]{1,}[,]?([0-9]{1,2})?$/).test(String(value)) || !value) props.onChange(e);
    }
    const onBlurNumberHandle = (e) => {
        const { value } = e.target;
        if (value.endsWith(',')) e.target.value = value.replace(',', '');
        props.onChange(e);
    }

    if (!props.settings) {
        return <></>
    }

    const { type, pattern, isMultiline, required, values, minValue, maxValue, placeholder, hasEmptyValue } = props.settings;
    const name = props.parentName ? `${props.parentName}---${props.settings.name}` : props.settings.name;
    const value = props.value;

    const disabled = props.settings.disabled || props.formMode === formModes.read;

    switch (type) {
        case formFieldTypes.crop:
            return (<CropInput value={value} name={name} onChange={props.onChange} disabled={disabled} />);
        case formFieldTypes.string:
        case formFieldTypes.int:
            switch (pattern) {
                case formStringFieldPatterns.number:
                    return (<input className="form-control" value={value?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} name={name} onChange={changeInputValueHandle} onBlur={(e) => onBlurNumberHandle(e)} disabled={disabled ? "disabled" : ""} />);
                case formStringFieldPatterns.iBan:
                    return (<InputMask mask="UA___________________________" replacement={{ _: /\d/ }} name={name} className="form-control" value={value ? value : "UA"} onKeyDown={onKeyDown} onChange={props.onChange} disabled={disabled ? "disabled" : ""} />);
                case formStringFieldPatterns.float:
                    return (<FloatField value={value?.toString()} className="form-control" name={name} onChange={props.onChange} disabled={disabled ? "disabled" : ""} accuracy={2} onlyPositiove={true} />);
                default:
                    return (<>
                        {isMultiline ?
                            <TextAreaInput name={name} className="form-control" value={value ?? ''} rows="4" maxLen={200} onChange={props.onChange} disabled={disabled ? "disabled" : ""} /> :
                            <input className="form-control" pattern={pattern} type={(type === formFieldTypes.int ? "number" : "")} value={value ?? ''} name={name} onKeyDown={onKeyDown} onChange={props.onChange} disabled={disabled ? "disabled" : ""} placeholder={props.settings.placeholder} />
                        }
                    </>);
            }
        case formFieldTypes.user:
            return (<div className="user-form-control">
                {userImage && <img src={userImage} />}
                <input className="form-control user" value={value} name={name} onChange={props.onChange} disabled={disabled ? "disabled" : ""} />
            </div>);
        case formFieldTypes.userInput:
            return (<UserInput value={value} name={name} onChange={props.onChange} disabled={disabled ? "disabled" : ""} placeholder={placeholder} valueFieldName={props.settings?.valueFieldName} allowSelfSelect={props.settings?.allowSelfSelect} subordinatesOnly={props.settings?.subordinatesOnly} />);
        case formFieldTypes.datetime:
            return (<DateTimeInput className="form-control" disabled={disabled} onlyDate={true} value={value} required={required} min={minValue} max={maxValue} format="date" name={name} placeholder={placeholder} onChange={props.onChange} />);
        case formFieldTypes.select:
            return (<SelectInput name={name} value={value ?? ''} values={values} canSearch={props.settings?.canSearch} hasEmptyValue={hasEmptyValue} placeholder={placeholder} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.collapsedSelect:
            return (<CollapsedSelectInput name={name} value={value} values={values} placeholder={placeholder} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.radioButtonGroup:
            return (<RadioButtonGroupInput name={name} value={value} values={values} placeholders={props.settings.placeholders} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.transportSelect:
            return (<TransportSelectInput name={name} value={value} values={values} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.destination:
            return (<TripDestinationInput name={name} value={value} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.startingPoint:
            return (<StartingPointInput name={name} value={value} settings={props.settings.settings} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.dateIntervalInput:
            return (<DateIntervalInput name={name} value={value} settings={props.settings.settings} hideStartDate={props.settings.hideStartDate}
                hideEndDate={props.settings.hideEndDate} minValue={minValue} maxValue={maxValue} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.route:
            return (<RouteInput name={name} value={value} settings={props.settings.settings} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.bool:
            return (<div className="checkbox-form">
                <div>
                    <input id={name} name={name} checked={value?.toString().toLowerCase() === 'true'} className="form-control" onChange={props.onChange} type="checkbox" disabled={disabled ? "disabled" : ""} />
                    <label htmlFor={name}></label>
                </div>
            </div>);
        case formFieldTypes.switcher:
            return (<SwitcherInput name={name} value={value} label={props.settings.label} settings={props.settings.settings} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.doublecheckbox:
            return (<DoubleCheckboxInput name={name} value={value} disabled={disabled} onChange={props.onChange} />);
        case formFieldTypes.warningcheckbox:
            return <WarningCheckBox name={name} value={value} onChange={props.onChange} disabled={disabled} warningText={props.settings.warningText} />;
        case formFieldTypes.file: return (<>
            <label className="upload-file-label upload-file-btn" onClick={() => document.getElementById(name).click()}>{i18next.t("addFile")}</label>
            <input className="file-select" name="file" id={name} onChange={props.onChange} type="file" style={{ display: "none" }} />
        </>);
        case formFieldTypes.documents:
            return (<DocumentSelectForm name={name} value={value} onChange={props.onChange} fileTypes={props.settings.fileTypes} disabled={disabled} />);
        case formFieldTypes.info:
            return (value && <div className="info-input">{value}</div>);
        case formFieldTypes.infoBox:
            return <InfoBox text={props.settings.label} />;
        case formFieldTypes.textInfo:
            return <div className="text-info-component" dangerouslySetInnerHTML={{ __html: props.settings.label }} />;
        case formFieldTypes.label:
            return <></>;
        case formFieldTypes.attachments:
            // AllowLinks і AllowPhoto — поле, в яке додаються не тільки готові файли
            if (props.settings.allowLinks || props.settings.allowPhoto) {
                return <AttachmentsInput name={name} value={value} helpText={props.settings.helpText} allowLinks={props.settings.allowLinks}
                    allowPhoto={props.settings.allowPhoto} noFilesLabel={props.settings.noFilesLabel} required={required}
                    submitted={props.submitted} onChange={props.onChange} disabled={disabled} />;
            }
            return <AttachmentsForm name={name} value={value} onChange={props.onChange} docId={props.settings.docId} noFilesLabel={props.settings.noFilesLabel} addBtnLabel={props.settings.addBtnLabel} maxCount={props.settings.maxCount} minCount={props.settings.minCount} minCountErrorText={props.settings.minCountErrorText} submitted={props.submitted} required={required} formMode={props.formMode} disabled={disabled} />;
        case formFieldTypes.subform:
            return <SubForm {...props} name={name} onChange={props.onChange} value={value} />
    }
}

export default FormField;